import type { CommissionType } from '../../api/salesCommision';
import type { EtcBonusAmountType, RevenueAdjustment, SalaryCalcData } from '../../api/salaryCalc';

/**
 * 급여 계산식 — 강사별 화면과 전체 강사 표가 함께 쓰는 단 한 벌.
 *
 * 기존 SalaryCalculationPage 의 식을 글자 그대로 옮겼다(리팩터 동등성 테스트로 고정).
 * - 매출 = totalSales + groupSales + 매출 조정 합.
 *   서버 totalSales 에 그룹 매출이 이미 들어 있어 그룹 매출이 두 번 더해진다(기존 결함, 별건).
 *   강사별 화면과 같은 숫자를 보여야 하므로 여기서 고치지 않는다 — 고치면 급여가 바뀐다.
 * - 수업료 = totalScheduleFee + groupScheduleFee. 서버 값 그대로(재계산 금지).
 *   저장 전 매출 조정은 수업료 구간을 바꾸지 않는다(기존 미리보기 동작).
 * - 인센티브: 비율이면 floor(매출 × 비율/100), 고정이면 floor(값).
 * - 기타 수당: 항목마다 floor 한 뒤 합한다. 비율 항목의 부가세 기준액도 먼저 floor.
 * - 공제: floor((인센티브 + 수업료 + 기본급 + 기타 수당) × 0.033). 실제 차감은 과세일 때만.
 */

export type VatPolicy = 'inclusive' | 'exclusive';

export type SalaryEtcBonusInput = {
    amount: number;
    amountType?: EtcBonusAmountType;
    vatIncluded?: boolean;
};

/** 화면에서 고친 값. 들어온 키만 서버 값 대신 쓴다(표는 넘기지 않는다 — 서버 값 그대로). */
export type SalaryEdits = {
    incentive?: number;
    incentiveType?: CommissionType;
    baseSalary?: number;
    etcBonus?: SalaryEtcBonusInput[];
    isTaxable?: boolean;
    revenueAdjustments?: Pick<RevenueAdjustment, 'amount'>[];
};

export type ComputeSalaryInput = {
    data: Partial<SalaryCalcData> | undefined | null;
    edits?: SalaryEdits;
    vatPolicy: VatPolicy;
    vatRate: number;
};

export type ComputeSalaryResult = {
    /** 자동 집계 매출(조정 전) */
    autoSales: number;
    totalRevenueAdjustment: number;
    /** 매출 */
    settlement: number;
    incentiveKRW: number;
    classFee: number;
    baseSalary: number;
    /** 기타 수당 항목별 원 금액(입력 순서 그대로) */
    etcBonusItemsKRW: number[];
    etcBonusKRW: number;
    /** 공제액(과세 여부와 무관하게 계산된 값 — 강사별 화면 표시용) */
    deduction: number;
    /** 실제 차감된 공제(과세일 때만, 아니면 0) */
    deductionApplied: number;
    finalSalary: number;
    isTaxable: boolean;
};

/** 센터 부가세 정책 — 강사별 화면과 같은 출처(authObject)·같은 기본값 */
export const resolveVatSettings = (authObject: any): { vatPolicy: VatPolicy; vatRate: number } => ({
    vatPolicy: authObject?.vatPolicy === 'exclusive' ? 'exclusive' : 'inclusive',
    vatRate: authObject?.vatRate ?? 10,
});

export const computeVatBases = (settlement: number, vatPolicy: VatPolicy, vatRate: number) => ({
    vatIncludedBasis: vatPolicy === 'inclusive' ? settlement : Math.floor(settlement * (1 + vatRate / 100)),
    vatExcludedBasis: vatPolicy === 'exclusive' ? settlement : Math.floor(settlement / (1 + vatRate / 100)),
});

/** 기타 수당 한 항목의 원 금액 */
export const etcBonusToKRW = (
    bonus: SalaryEtcBonusInput,
    settlement: number,
    vatPolicy: VatPolicy,
    vatRate: number,
): number => {
    if (bonus.amountType !== 'percentage') return Math.floor(bonus.amount);
    const { vatIncludedBasis, vatExcludedBasis } = computeVatBases(settlement, vatPolicy, vatRate);
    const basis = bonus.vatIncluded === false ? vatExcludedBasis : vatIncludedBasis;
    return Math.floor(basis * (bonus.amount / 100));
};

/** 인센티브 원 금액 */
export const incentiveToKRW = (incentive: number, incentiveType: CommissionType | undefined, settlement: number) =>
    incentiveType === 'percentage' ? Math.floor(settlement * (incentive / 100)) : Math.floor(incentive);

const pick = <K extends keyof SalaryEdits>(edits: SalaryEdits | undefined, key: K): boolean =>
    !!edits && Object.prototype.hasOwnProperty.call(edits, key);

export const computeSalary = ({ data, edits, vatPolicy, vatRate }: ComputeSalaryInput): ComputeSalaryResult => {
    const incentive = (pick(edits, 'incentive') ? edits!.incentive : data?.incentive) as number;
    const incentiveType = (pick(edits, 'incentiveType') ? edits!.incentiveType : data?.incentiveType) as CommissionType;
    const baseSalary = (pick(edits, 'baseSalary') ? edits!.baseSalary : data?.baseSalary) as number;
    const etcBonus = ((pick(edits, 'etcBonus') ? edits!.etcBonus : data?.etcBonus) ?? []) as SalaryEtcBonusInput[];
    const isTaxable = !!(pick(edits, 'isTaxable') ? edits!.isTaxable : data?.isTaxable);
    const revenueAdjustments = ((pick(edits, 'revenueAdjustments') ? edits!.revenueAdjustments : data?.revenueAdjustments) ??
        []) as Pick<RevenueAdjustment, 'amount'>[];

    const groupSales = data?.groupSales ?? 0;
    const totalRevenueAdjustment = revenueAdjustments.reduce((acc, cur) => acc + cur.amount, 0);
    const settlement = (data?.totalSales ?? 0) + groupSales + totalRevenueAdjustment;
    const autoSales = (data?.totalSales ?? 0) + groupSales;

    const groupScheduleFee = data?.groupScheduleFee ?? 0;
    const classFee = (data?.totalScheduleFee ?? 0) + groupScheduleFee;

    const etcBonusItemsKRW = etcBonus.map((bonus) => etcBonusToKRW(bonus, settlement, vatPolicy, vatRate));
    const etcBonusKRW = etcBonusItemsKRW.reduce((acc, cur) => acc + cur, 0);

    const incentiveKRW = incentiveToKRW(incentive, incentiveType, settlement);

    const deduction = Math.floor((incentiveKRW + classFee + baseSalary + etcBonusKRW) * 0.033);
    const finalSalary = isTaxable
        ? incentiveKRW + classFee + baseSalary + etcBonusKRW - deduction
        : incentiveKRW + classFee + baseSalary + etcBonusKRW;

    return {
        autoSales,
        totalRevenueAdjustment,
        settlement,
        incentiveKRW,
        classFee,
        baseSalary,
        etcBonusItemsKRW,
        etcBonusKRW,
        deduction,
        deductionApplied: isTaxable ? deduction : 0,
        finalSalary,
        isTaxable,
    };
};
